import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { User, LogOut, Settings, Crown } from 'lucide-react';

export const Header = () => {
    const { user, isAuthenticated, isPremium, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const isActive = (path) => location.pathname === path;

    return (
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#071d36]/90 backdrop-blur-md border-b border-[#334155]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo - Prominent size matching Get Started button */}
                    <Link 
                        to="/" 
                        className="flex items-center group"
                        data-testid="logo-link"
                    >
                        <img 
                            src="https://customer-assets.emergentagent.com/job_181a1683-971b-4c0d-99ad-dbc6848d04b9/artifacts/2a99u289_AB%20Header%20Logo%20transparent.png"
                            alt="AirlineBound"
                            className="h-14 sm:h-16 w-auto object-contain transition-transform group-hover:scale-105"
                        />
                    </Link>

                    {/* Navigation */}
                    <nav className="hidden md:flex items-center gap-6">
                        <Link
                            to="/"
                            className={`text-sm font-medium transition-colors ${
                                isActive('/') 
                                    ? 'text-[#f07050]' 
                                    : 'text-[#94a3b8] hover:text-white'
                            }`}
                            data-testid="nav-home"
                        >
                            Home
                        </Link>
                        <Link
                            to="/tracker"
                            className={`text-sm font-medium transition-colors ${
                                isActive('/tracker') 
                                    ? 'text-[#f07050]' 
                                    : 'text-[#94a3b8] hover:text-white'
                            }`}
                            data-testid="nav-tracker"
                        >
                            Flight Tracker
                        </Link>
                    </nav>

                    {/* Auth Section */}
                    <div className="flex items-center gap-4">
                        {isAuthenticated ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button 
                                        variant="ghost" 
                                        className="flex items-center gap-2 text-[#94a3b8] hover:text-white"
                                        data-testid="user-menu-trigger"
                                    >
                                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#1e293b] border border-[#334155]">
                                            <User className="h-4 w-4" />
                                        </div>
                                        <span className="hidden sm:inline font-medium">
                                            {user?.username}
                                        </span>
                                        {isPremium && (
                                            <Crown className="h-4 w-4 text-[#f07050]" />
                                        )}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent 
                                    align="end" 
                                    className="w-48 bg-[#1e293b] border-[#334155]"
                                >
                                    <DropdownMenuItem 
                                        onClick={() => navigate('/profile')}
                                        className="cursor-pointer hover:bg-[#334155]"
                                        data-testid="menu-profile"
                                    >
                                        <User className="mr-2 h-4 w-4" />
                                        Profile
                                    </DropdownMenuItem>
                                    {!isPremium && (
                                        <DropdownMenuItem 
                                            onClick={() => navigate('/profile')}
                                            className="cursor-pointer hover:bg-[#334155] text-[#f07050]"
                                            data-testid="menu-upgrade"
                                        >
                                            <Crown className="mr-2 h-4 w-4" />
                                            Upgrade to Premium
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator className="bg-[#334155]" />
                                    <DropdownMenuItem 
                                        onClick={handleLogout}
                                        className="cursor-pointer hover:bg-[#334155] text-red-400"
                                        data-testid="menu-logout"
                                    >
                                        <LogOut className="mr-2 h-4 w-4" />
                                        Logout
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <div className="flex items-center gap-3">
                                <Button
                                    variant="ghost"
                                    onClick={() => navigate('/login')}
                                    className="text-[#94a3b8] hover:text-white"
                                    data-testid="login-btn"
                                >
                                    Login
                                </Button>
                                <Button
                                    onClick={() => navigate('/register')}
                                    className="bg-[#f07050] hover:bg-[#e06040] text-white btn-primary-glow"
                                    data-testid="register-btn"
                                >
                                    Get Started
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};
